/**
 * Système de logging centralisé utilisant la librairie 'loglevel'
 * Organisé par composant/module de l'application
 *
 * Usage:
 * import { logger } from './utils/logger.js';
 *
 * logger.timeline.info('Message timeline');
 * logger.timeline.filters.debug('Détail filtres');
 * logger.map.stores.debug('Store map');
 *
 * Contrôle dans la console navigateur:
 * localStorage.logLevel = 'DEBUG'; // Afficher tous les niveaux
 * localStorage.logLevel = 'INFO';  // Afficher INFO, WARN, ERROR
 * localStorage.logLevel = 'WARN';  // Afficher WARN, ERROR seulement
 * localStorage.logLevel = 'ERROR'; // Afficher ERROR seulement
 * localStorage.logLevel = 'SILENT'; // Aucun message
 *
 * Pour activer des composants spécifiques:
 * localStorage.geneafanDebugComponents = 'statistics,timeline,map'; // Composants séparés par virgule
 * localStorage.geneafanDebugComponents = '*'; // Tous les composants
 */

import log from 'loglevel';

// Configuration initiale de loglevel
if (typeof window !== 'undefined') {
    // Exposer loglevel globalement
    window.loglevel = log;

    // Configurer le niveau par défaut depuis localStorage
    const savedLevel = localStorage.logLevel || 'INFO';
    try {
        log.setLevel(savedLevel);
    } catch (e) {
        log.setLevel('INFO');
    }

    // Configurer les composants actifs
    window.geneafanDebugComponents = localStorage.geneafanDebugComponents || '*';
}

/**
 * Vérifie si un composant est activé pour le debug
 */
function isComponentEnabled(componentName) {
    if (typeof window === 'undefined') return false;

    const activeComponents = window.geneafanDebugComponents || '*';
    if (activeComponents === '*') return true;

    return activeComponents.split(',').some(comp =>
        comp.trim() === componentName ||
        componentName.includes(comp.trim())
    );
}

/**
 * Crée des loggers avec différents niveaux pour un namespace
 */
function createLoggers(namespace) {
    // Extraire le nom du composant (ex: 'geneafan:statistics' -> 'statistics')
    const componentName = namespace.replace('geneafan:', '').split(':')[0];

    const createLogger = (level, emoji = '', color = '#333') => {
        return (...args) => {
            if (!isComponentEnabled(componentName)) return;

            const prefix = `%c[${namespace}]%c ${emoji}`;
            const styles = [`color: ${color}; font-weight: bold`, 'color: inherit'];

            switch (level) {
                case 'debug':
                    log.debug(prefix, ...styles, ...args);
                    break;
                case 'info':
                    log.info(prefix, ...styles, ...args);
                    break;
                case 'warn':
                    log.warn(prefix, ...styles, ...args);
                    break;
                case 'error':
                    log.error(prefix, ...styles, ...args);
                    break;
                default:
                    log.info(prefix, ...styles, ...args);
            }
        };
    };

    return {
        // Niveau de base - toujours visible si le namespace est activé
        info: createLogger('info', '📁', '#FF0000'),

        // Sous-niveaux pour plus de granularité
        debug: createLogger('debug', '🔍', '#FF9933'),
        warn: createLogger('warn', '⚠️', '#FFA500'),
        error: createLogger('error', '❌', '#FF0000'),
        success: createLogger('info', '✅', '#00CC66'),
        perf: createLogger('info', '⚡', '#00CC99'),

        // Méthodes utilitaires
        group: (title) => {
            if (!isComponentEnabled(componentName)) return;
            log.info(`%c[${namespace}]%c 📁 ${title}`, 'color: #FF0000; font-weight: bold', 'color: inherit');
        },
        groupEnd: () => {
            // Compatible avec l'ancienne API mais ne fait rien
        },
        time: (label) => {
            if (!isComponentEnabled(componentName)) return;
            log.info(`%c[${namespace}]%c ⏱️ Starting timer: ${label}`, 'color: #FF0000; font-weight: bold', 'color: inherit');
        },
        timeEnd: (label) => {
            if (!isComponentEnabled(componentName)) return;
            log.info(`%c[${namespace}]%c ⏱️ Timer ended: ${label}`, 'color: #FF0000; font-weight: bold', 'color: inherit');
        }
    };
}

// Logger organisé par composants
export const logger = {
    // Application Initialization (core startup)
    initialization: {
        ...createLoggers('geneafan:initialization'),
        context: createLoggers('geneafan:initialization:context'),
        ui: createLoggers('geneafan:initialization:ui'),
        main: createLoggers('geneafan:initialization:main'),
        bootstrap: createLoggers('geneafan:initialization:bootstrap'),
        auth: createLoggers('geneafan:initialization:auth')
    },

    // Timeline Component (tabs/timeline/)
    timeline: {
        ...createLoggers('geneafan:timeline'),
        manager: createLoggers('geneafan:timeline:manager'),
        store: createLoggers('geneafan:timeline:store'),
        events: createLoggers('geneafan:timeline:events'),
        filters: createLoggers('geneafan:timeline:filters'),
        ui: createLoggers('geneafan:timeline:ui')
    },
    
    // Map Component (tabs/familyMap/)
    map: {
        ...createLoggers('geneafan:map'),
        stores: createLoggers('geneafan:map:stores'),
        familyTowns: createLoggers('geneafan:map:familyTowns'),
        surnames: createLoggers('geneafan:map:surnames'),
        ancestors: createLoggers('geneafan:map:ancestors'),
        google: createLoggers('geneafan:map:google')
    },
    
    // Fan Chart Component (tabs/fanChart/)
    fan: {
        ...createLoggers('geneafan:fan'),
        config: createLoggers('geneafan:fan:config'),
        draw: createLoggers('geneafan:fan:draw'),
        manager: createLoggers('geneafan:fan:manager')
    },
    
    // Family Tree Component (tabs/familyTree/)
    tree: {
        ...createLoggers('geneafan:tree'),
        manager: createLoggers('geneafan:tree:manager')
    },
    
    // Statistics Component (tabs/statistics/)
    statistics: {
        ...createLoggers('geneafan:statistics'),
        service: createLoggers('geneafan:statistics:service'),
        worker: createLoggers('geneafan:statistics:worker')
    },
    
    // Core Data (stores communs)
    gedcom: {
        ...createLoggers('geneafan:gedcom'),
        store: createLoggers('geneafan:gedcom:store'),
        parse: createLoggers('geneafan:gedcom:parse'),
        process: createLoggers('geneafan:gedcom:process')
    },
    
    // Root Person Store
    root: {
        ...createLoggers('geneafan:root'),
        store: createLoggers('geneafan:root:store'),
        hierarchy: createLoggers('geneafan:root:hierarchy')
    },
    
    // Cache System  
    cache: {
        ...createLoggers('geneafan:cache'),
        service: createLoggers('geneafan:cache:service'),
        restore: createLoggers('geneafan:cache:restore'),
        save: createLoggers('geneafan:cache:save')
    },
    
    // Upload & Network
    upload: {
        ...createLoggers('geneafan:upload'),
        manager: createLoggers('geneafan:upload:manager'),
        worker: createLoggers('geneafan:upload:worker')
    },

    // AI Notes Extraction
    ai: {
        ...createLoggers('geneafan:ai'),
        notes: createLoggers('geneafan:ai:notes'),
        extraction: createLoggers('geneafan:ai:extraction')
    },

    // Tab Management
    tabs: {
        ...createLoggers('geneafan:tabs'),
        manager: createLoggers('geneafan:tabs:manager'),
        content: createLoggers('geneafan:tabs:content'),
        loader: createLoggers('geneafan:tabs:loader')
    },
    
    // System
    events: createLoggers('geneafan:events'),
    performance: createLoggers('geneafan:performance'),
    auth: createLoggers('geneafan:auth'),
    ui: createLoggers('geneafan:ui')
};

/**
 * Configuration rapide pour développement
 */
export const debugConfig = {
    presets: {
        // Tout
        all: 'geneafan:*',
        quiet: '',
        
        // Par composant
        timeline: 'geneafan:timeline*',
        map: 'geneafan:map*',
        fan: 'geneafan:fan*',
        tree: 'geneafan:tree*',
        statistics: 'geneafan:statistics*',
        
        // Par système
        cache: 'geneafan:cache*',
        gedcom: 'geneafan:gedcom*',
        upload: 'geneafan:upload*',
        root: 'geneafan:root*',
        
        // Combinaisons pour debug spécifique
        timelineDebug: 'geneafan:timeline*,geneafan:cache*,geneafan:root*',
        mapDebug: 'geneafan:map*,geneafan:gedcom:store*',
        cacheDebug: 'geneafan:cache*,geneafan:gedcom*,geneafan:upload*',
        
        // Performance
        performance: 'geneafan:performance*,geneafan:*:perf*'
    },
    
    set: (preset) => {
        const config = debugConfig.presets[preset];
        if (config !== undefined) {
            localStorage.debug = config;
            console.log(`🔧 Debug configuré: ${preset} -> "${config}"`);
            console.log('✅ Les nouveaux logs vont maintenant apparaître');
        } else {
            console.warn(`❌ Preset inconnu: ${preset}`);
            console.log('📋 Presets disponibles:', Object.keys(debugConfig.presets));
        }
    },
    
    current: () => {
        const current = localStorage.debug || '';
        console.log(`🔍 Configuration actuelle: "${current}"`);
        return current;
    }
};

// Exposer globalement pour faciliter le debug en console
if (typeof window !== 'undefined') {
    window.debugConfig = debugConfig;
    window.geneafanLogger = logger;
}

// Afficher un message d'aide au premier chargement
if (typeof window !== 'undefined' && !localStorage.getItem('geneafan_debug_help_shown')) {
    console.log(`
🔧 GeneaFan Debug System - Organisé par composants

Exemples d'usage:
- logger.timeline.info('Message timeline')
- logger.timeline.filters.debug('Détail filtres')
- logger.map.stores.info('Info stores map')
- logger.cache.service.debug('Service cache')

Contrôle rapide:
- debugConfig.set('timeline') - Tout le composant timeline
- debugConfig.set('map') - Tout le composant map  
- debugConfig.set('cache') - Système de cache
- debugConfig.set('all') - Tous les logs
- debugConfig.set('quiet') - Aucun log

Presets: ${Object.keys(debugConfig.presets).join(', ')}
    `);
    localStorage.setItem('geneafan_debug_help_shown', 'true');
}

export default logger;